import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { CONTACT_INFO, siteImages } from '../data/mock';
import { Target, Users, Award, Heart, Sparkles, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AboutPage = () => {
  useEffect(() => {
    document.title = 'About Us | Radiant Bliss Aura';
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        {/* Banner Section */}
        <section className="keep-dark relative h-64 md:h-80 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.aboutBanner}
              alt="About Us"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark-950 via-dark-950/80 to-dark-950/60" />
          </div>
          <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-500/10 border border-athletic-500/30 rounded-full text-athletic-400 text-xs font-semibold mb-4">
                <Sparkles className="w-3.5 h-3.5" />
                OUR STORY
              </div>
              <h1 className="font-display text-4xl md:text-5xl text-white font-extrabold mb-3">
                About Radiant Bliss Aura
              </h1>
              <p className="text-dark-300 max-w-lg">
                Premium clean skincare for skincare lovers who demand excellence
              </p>
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Mission Section */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-20">
            <div>
              <span className="inline-block px-4 py-1.5 bg-athletic-500/10 text-athletic-400 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-athletic-500/20">
                Our Mission
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-white font-extrabold mb-6">
                Bringing Out the <span className="text-athletic-400">Best in You</span>
              </h2>
              <p className="text-dark-300 text-lg leading-relaxed mb-6">
                At Radiant Bliss Aura, we believe that the right equipment can transform your ritual. Our mission is to provide skincare lovers with premium clean skincare that helps them achieve their goals.
              </p>
              <p className="text-dark-300 leading-relaxed mb-8">
                We carefully select each product in our catalog, ensuring it meets our high standards for quality, durability, and performance. Whether you're ritual at home or pushing your limits outdoors, we have the gear to support your journey.
              </p>
              <div className="flex flex-wrap gap-4">
                {['Quality First', 'Customer Focused', 'Performance Driven'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 px-4 py-2 bg-dark-900 rounded-xl border border-dark-800">
                    <CheckCircle2 className="w-4 h-4 text-athletic-400" />
                    <span className="text-white text-sm font-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-2xl overflow-hidden border border-dark-800">
                <img 
                  src={siteImages.heroLifestyle}
                  alt="Athletes ritual"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-athletic-500 text-white px-6 py-4 rounded-2xl shadow-lg">
                <p className="font-display font-extrabold text-2xl">58+</p>
                <p className="text-sm font-semibold">Premium Products</p>
              </div>
            </div>
          </div>

          {/* Core Values */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 bg-athletic-500/10 text-athletic-400 text-xs font-bold uppercase tracking-wider rounded-full mb-4 border border-athletic-500/20">
                Core Values
              </span>
              <h2 className="font-display text-3xl lg:text-4xl text-white font-extrabold">
                What Drives Us
              </h2>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Target, title: 'Quality First', desc: 'Every product is carefully selected for performance and durability' },
                { icon: Users, title: 'Customer Focus', desc: 'Your success is our priority. We\'re here to support your journey' },
                { icon: Award, title: 'Excellence', desc: 'We strive for excellence in everything we do' },
                { icon: Heart, title: 'Passion', desc: 'We\'re passionate about fitness and helping skincare lovers succeed' },
              ].map((value, i) => (
                <div key={i} className="bg-dark-900 border border-dark-800 rounded-2xl p-6 hover:border-athletic-500/50 transition-all group">
                  <div className="w-14 h-14 bg-athletic-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-athletic-500/20 transition-colors">
                    <value.icon className="w-7 h-7 text-athletic-400" />
                  </div>
                  <h3 className="font-display font-bold text-white text-lg mb-2">{value.title}</h3>
                  <p className="text-dark-400 text-sm leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Why Choose Us */}
          <div className="bg-dark-900 border border-dark-800 rounded-2xl p-8 md:p-12">
            <div className="text-center mb-10">
              <h2 className="font-display text-3xl lg:text-4xl text-white font-extrabold mb-4">
                Why Choose Radiant Bliss Aura?
              </h2>
              <p className="text-dark-400 max-w-2xl mx-auto">
                We're committed to providing the best clean skincare and customer experience
              </p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                'Premium quality clean skincare',
                'Fast shipping across the USA',
                'Secure checkout with SSL encryption',
                '30-day hassle-free returns',
                'Dedicated customer support',
                'Carefully curated product selection',
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3 p-4 bg-dark-800 rounded-xl">
                  <CheckCircle2 className="w-5 h-5 text-athletic-400 flex-shrink-0" />
                  <span className="text-white font-medium">{item}</span>
                </div>
              ))}
            </div>
            <div className="text-center mt-10">
              <Link 
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-athletic-500 to-athletic-600 text-white font-bold uppercase tracking-wider rounded-xl hover:from-athletic-400 hover:to-athletic-500 transition-all shadow-lg"
              >
                Shop Our Products
              </Link>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AboutPage;
